import { ApiError } from "@/lib/api/client";
import type { ChecksSummary, PullRequestView } from "@/lib/api/pull-requests";

/** The error code and `details.reason` an API error carries, if any. */
export function apiErrorInfo(error: unknown): { code: string | null; reason: string | null; message: string } {
  if (!(error instanceof ApiError)) {
    return { code: null, reason: null, message: error instanceof Error ? error.message : "Something went wrong" };
  }
  const body = error.data as { error?: { code?: unknown; details?: { reason?: unknown } } } | undefined;
  const code = typeof body?.error?.code === "string" ? body.error.code : null;
  const reason = typeof body?.error?.details?.reason === "string" ? body.error.details.reason : null;
  return { code, reason, message: error.message };
}

/** Checks in plain words, e.g. "All 5 checks passed". */
export function describeChecks(checks: ChecksSummary | null): { text: string; tone: "good" | "bad" | "waiting" | "none" } {
  if (!checks || checks.total === 0) return { text: "No automated checks ran on this change", tone: "none" };
  if (checks.failed > 0) {
    return {
      text: `${String(checks.failed)} of ${String(checks.total)} checks failed`,
      tone: "bad",
    };
  }
  if (checks.pending > 0) {
    return {
      text: `${String(checks.pending)} of ${String(checks.total)} checks still running`,
      tone: "waiting",
    };
  }
  return {
    text: checks.total === 1 ? "The check passed" : `All ${String(checks.total)} checks passed`,
    tone: "good",
  };
}

/** GitHub's mergeability in plain words. */
export function describeMergeability(
  pr: Pick<PullRequestView, "state" | "mergeable" | "mergeableState" | "baseRef">
): string {
  if (pr.state === "merged") return "This change has been merged";
  if (pr.state === "closed") return "This pull request was closed without merging";
  if (pr.mergeable === false || pr.mergeableState === "dirty") {
    return `This change conflicts with ${pr.baseRef}`;
  }
  switch (pr.mergeableState) {
    case "clean":
      return "Ready to merge";
    case "behind":
      return `Ready to merge, but ${pr.baseRef} has newer changes`;
    case "blocked":
      return "Blocked by the repository's rules (required reviews or checks)";
    case "unstable":
      return "Can be merged, but some checks did not pass";
    case "draft":
      return "Still a draft";
    case "has_hooks":
      return "Ready to merge";
    default:
      return "GitHub is still checking whether this can be merged";
  }
}

export function hasConflicts(pr: Pick<PullRequestView, "state" | "mergeable" | "mergeableState">): boolean {
  return pr.state === "open" && (pr.mergeable === false || pr.mergeableState === "dirty");
}

/**
 * Why Merge is unavailable, in plain words — or null when it can be tried.
 * The server still has the final say (it rechecks everything on merge).
 */
export function mergeBlockedReason(pr: PullRequestView): string | null {
  if (pr.state === "merged") return "Already merged.";
  if (pr.state === "closed") return "This pull request is closed.";
  if (pr.allowedMergeMethods.length === 0) return "This repository doesn't allow any merge method.";
  if (hasConflicts(pr)) return `It conflicts with ${pr.baseRef}. Resolve the conflicts first.`;
  if (pr.mergeableState === "draft") return "It's still a draft.";
  if (pr.mergeableState === "blocked") return "The repository's rules block merging (required reviews or checks).";
  if (pr.mergeable === null) return "GitHub is still checking whether this can be merged.";
  return null;
}

export const MERGE_METHOD_LABELS = {
  squash: "Squash and merge",
  merge: "Create a merge commit",
  rebase: "Rebase and merge",
} as const;

export const MERGE_BLOCKED_MESSAGES: Record<string, string> = {
  HEAD_CHANGED: "New commits landed since you opened this page. Review the latest changes and try again.",
  MIGRATIONS_NOT_CONFIRMED: "Confirm the database changes have been applied before merging.",
  BACKUP_NOT_CONFIRMED:
    "This merge includes a destructive database change. Back up the database and confirm the backup before merging. If new commits were pushed since you last confirmed, confirm it again.",
  DESTRUCTIVE_MIGRATION_NOT_APPROVED:
    "This merge includes a database change that can lose data. The project owner must approve it before merging. If new commits were pushed since it was approved, it must be approved again.",
  CONFLICT: "This change conflicts with the base branch. Resolve the conflicts first.",
  CHECKS_REQUIRED: "Required checks haven't passed yet.",
  BRANCH_PROTECTED: "The repository's rules block this merge (for example, a required review).",
  METHOD_NOT_ALLOWED: "The repository doesn't allow that merge method.",
};

export function shortSha(sha: string | null | undefined): string {
  return sha ? sha.slice(0, 7) : "";
}
